import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetch as fetchUsers } from "services/user-select.service";
import { fetchCustomersListWithHideCheck as fetchCustomersList } from "services/customers.service";
import { AppDispatch, RootState } from "configureStore";

interface User {
  id: string;
  name: string;
}

interface FiltersValue {
  [key: string]: string;
}

interface CustomersState {
  customersCollection: any[];
  isLoading: boolean;
  isError: boolean;
  errMsg: string | null;
  queryValue: string | null;
  users: Record<string, string>;
  filtersValue: FiltersValue;
}

const initialState: CustomersState = {
  customersCollection: [],
  isLoading: false,
  isError: false,
  errMsg: null,
  queryValue: null,
  users: {},
  filtersValue: {},
};

const CustomersSlice = createSlice({
  name: "customers-hide",
  initialState,
  reducers: {
    setLoading(state) {
      state.isLoading = true;
      state.isError = false;
    },
    setCustomers(state, action: PayloadAction<{ customersCollection: any[] }>) {
      state.isLoading = false;
      state.customersCollection = action.payload.customersCollection;
    },
    setUsers(state, action: PayloadAction<{ usersCollection: User[] }>) {
      action.payload.usersCollection.forEach(({ id, name }) => {
        state.users[id] = name;
      });
    },
    setError(state, action: PayloadAction<{ errMsg: string }>) {
      state.isError = true;
      state.errMsg = action.payload.errMsg;
    },
    setFilterValue(
      state,
      action: PayloadAction<{ key: string; value: string }>
    ) {
      state.filtersValue[action.payload.key] = action.payload.value;
    },
  },
});

export const { setLoading, setCustomers, setUsers, setError, setFilterValue } =
  CustomersSlice.actions;

export default CustomersSlice.reducer;

// Quick toggles and typing in the search start several loads; neither the
// answer nor the failure of an older load may reach the store, or it would show
// stale ticks or a false error.
let latestFetch = 0;

export const fetchCustomers =
  (companyId: string, filtersValue: FiltersValue, userId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const requestNo = ++latestFetch;
    dispatch(setLoading());
    try {
      const { customersCollection } = await fetchCustomersList(
        companyId,
        filtersValue,
        userId
      );
      if (requestNo !== latestFetch) return;
      dispatch(setCustomers({ customersCollection }));

      const { usersCollection } = await fetchUsers(companyId);
      dispatch(setUsers({ usersCollection }));
    } catch (e) {
      if (requestNo !== latestFetch) return;
      dispatch(setError({ errMsg: "An error occurred" }));
    }
  };
